import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";

import styles from "@/components/appointmentDashboard/appointmentTreeItemWidgets.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import Modal from "@/components/modal";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import FindingForm from "@/components/forms/findingForm";
import { api } from "@/helper/api";

interface Props {
  widgets: any;
  appointmentId: string;
  handleSetError?: (error: any) => void;
  handleReloadTrigger?: () => void;
}
export interface formValues {
  id?: string;
  created_by?: string;
  modified_by?: string;
  created_on?: string;
  modified_on?: string;
  unit?: string;
  name?: string;
  value?: number;
  appointment?: string;
  findings?: [];
}

const AppointmentTreeItemWidgets: FC<Props> = ({
  widgets,
  appointmentId,
  handleSetError,
  handleReloadTrigger,
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [findingId, setFindingId] = useState<string>("");

  const [formValues, setFormValues] = useState<formValues>();

  const [formIdForQuickEdit, setFormIdForQuickEdit] = useState<string>("");

  const { showConfirmationModal } = useConfirmationModal();

  const { t } = useTranslation();

  const getMeasurements = (appointmentId: string): Promise<void> => {
    const additionalUrlParameters: any = {};
    additionalUrlParameters["linked_appointment_id"] = appointmentId;
    return new Promise((resolve, reject) => {
      api.genericApiRequest({
        entity: "measurement",
        method: "get",
        getAllPages: true,

        successHandler: (res: any) => {
          setFormValues(res.data.results);
          resolve();
        },
        failHandler: (error: any) => {
          handleSetError?.(error);
          reject(error);
        },
        parametersToRender: {
          depth: "0",
          additionalUrlParameters: additionalUrlParameters,
        },
      });
    });
  };

  const renderWidgets = () => {
    const sortedData = [...widgets].sort((a: any, b: any) => {
      if (a?.widget_priority !== b?.widget_priority) {
        return a?.widget_priority - b?.widget_priority;
      }
      return a?.widget_title?.localeCompare(b?.widget_title);
    });

    const handleModalOnClose = () => {
      showConfirmationModal(t("confirm_modal_close"), {
        title: "",
        confirmButtonText: "",
        onConfirm: () => setModalOpen(false),
      });
    };

    const handleModalOpen = async (
      formId: any,
      findingId: any,
      modalTitle: string,
    ) => {
      if (!findingId) {
        showConfirmationModal(t("confirm_error_no_finding"));
        return;
      }
      setFormIdForQuickEdit(formId);
      setFindingId(findingId);
      setModalTitle(modalTitle);

      try {
        await getMeasurements(appointmentId);
        setModalOpen(true);
      } catch (error) {
        handleSetError?.(error);
      }
    };

    return (
      <>
        {sortedData.map((widget: any, index: any) => (
          <Grid key={index} item xs={12}>
            <Accordion
              className={styles.accordion}
              defaultExpanded={index === 0}
              sx={{
                "&.MuiAccordion-root.Mui-expanded": {
                  margin: "0",
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "0",
                },
                "& .MuiAccordionSummary-root": {
                  minHeight: "48px !important",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "0px 16px 16px 16px",
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div className={styles.widgetTitleWrapper}>
                  <h2 className={styles.accordionTitle}>
                    {widget.widget_title
                      ? widget.widget_title
                      : widget.form_name}
                  </h2>
                  <Tooltip title={t("edit")}>
                    <GenericButton
                      variant="icon"
                      color="iconBase"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModalOpen(
                          widget.form_id,
                          widget.finding_id,
                          widget.widget_title
                            ? widget.widget_title
                            : widget.form_name,
                        );
                      }}
                    >
                      <EditIcon />
                    </GenericButton>
                  </Tooltip>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container component={List} className={styles.noPadding}>
                  {widget.items.map((item: any, itemIndex: any) => {
                    return (
                      <Grid
                        item
                        component={ListItem}
                        key={itemIndex}
                        xs={3}
                        className={styles.noPadding}
                      >
                        <ListItemText
                          primary={
                            item?.measurement_definition?.short_label ||
                            item?.measurement_definition?.label
                          }
                          primaryTypographyProps={{
                            className: styles.primaryText,
                          }}
                          secondaryTypographyProps={{
                            className: styles.secondaryText,
                          }}
                          secondary={
                            Array.isArray(item.value)
                              ? item.value.length > 0
                                ? item.value.join(", ")
                                : "-"
                              : item.value === "yes"
                              ? t("yes")
                              : item.value === "no"
                              ? t("no")
                              : item.value || "-"
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}

        <Modal
          open={modalOpen}
          title={modalTitle}
          hideDialogActions={true}
          onClose={handleModalOnClose}
        >
          <FindingForm
            withPaper={false}
            findingId={findingId}
            formId={formIdForQuickEdit}
            formValues={formValues}
            onSuccessfulFormSubmitHandler={() => {
              setModalOpen(false);
              handleReloadTrigger?.();
            }}
            onCancelFormHandler={() => {
              setModalOpen(false);
            }}
          />
        </Modal>
      </>
    );
  };

  return <>{renderWidgets()}</>;
};

export default AppointmentTreeItemWidgets;
